
import {
  User, Organization, Incident, ActivityLog, PlatformSettings, LegalContent
} from '../types';

const API_BASE = '/api.php';

export const isProduction = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE}?action=ping`);
    const data = await response.json();
    return response.ok && data.status === 'ok';
  } catch {
    return false;
  }
};

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = sessionStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!response.ok) {
    // A 401 on login (bad credentials) or logout (session already gone) isn't
    // a session expiring mid-use - don't fire the global logout handler for those.
    const isAuthEndpoint = path.startsWith('?action=login') || path.startsWith('?action=logout');
    if (response.status === 401 && !isAuthEndpoint) {
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Server error: ${response.status}`);
  }
  return response.json();
}

export const logout = () => request<void>('?action=logout', { method: 'POST' });

export const changePassword = (currentPassword: string, newPassword: string) =>
  request<void>('?action=changePassword', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) });

export const forgotPassword = (email: string) =>
  request<{ status: string; message: string }>('?action=forgotPassword', { method: 'POST', body: JSON.stringify({ email }) });

export const resetPassword = (token: string, newPassword: string) =>
  request<void>('?action=resetPassword', { method: 'POST', body: JSON.stringify({ token, newPassword }) });

export const verifyEmail = (token: string) =>
  request<void>('?action=verifyEmail', { method: 'POST', body: JSON.stringify({ token }) });

export const login = async (email: string, password: string): Promise<User> => {
  const data = await request<{ user: User; token: string }>('?action=login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  sessionStorage.setItem('authToken', data.token);
  return data.user;
};

export const getIncidents = () => request<Incident[]>('?action=getIncidents');
export const createIncident = (data: any, _user?: User) => request<Incident>('?action=createIncident', { method: 'POST', body: JSON.stringify(data) });
export const updateIncident = (id: string, updates: any, _user?: User) => request<Incident>(`?action=updateIncident&id=${id}`, { method: 'POST', body: JSON.stringify(updates) });
export const deleteIncident = (id: string, _user?: User) => request<void>(`?action=deleteIncident&id=${id}`, { method: 'POST' });
export const resolveIncident = (id: string, notes: string, _user?: User) => request<Incident>(`?action=resolveIncident&id=${id}`, { method: 'POST', body: JSON.stringify({ notes }) });
export const disputeIncident = (id: string, reason: string, _user?: User) => request<Incident>(`?action=disputeIncident&id=${id}`, { method: 'POST', body: JSON.stringify({ reason }) });
export const getOrganizations = () => request<Organization[]>('?action=getOrganizations');
export const updateOrganizationStatus = (id: string, isActive: boolean, _user?: User) => request<Organization>(`?action=updateOrgStatus&id=${id}`, { method: 'POST', body: JSON.stringify({ isActive }) });
export const updateOrganization = (id: string, data: any, _user?: User) => request<Organization>(`?action=updateOrg&id=${id}`, { method: 'POST', body: JSON.stringify(data) });
export const deleteOrganization = (id: string, _user?: User) => request<void>(`?action=deleteOrg&id=${id}`, { method: 'POST' });
export const createOrganizationWithUser = (orgData: any, userData: any, _user?: User) => request<Organization>('?action=createOrgWithUser', { method: 'POST', body: JSON.stringify({ ...orgData, userData }) });
export const getPlatformSettings = () => request<PlatformSettings>('?action=getSettings');
export const updatePlatformSettings = (settings: PlatformSettings) => request<PlatformSettings>('?action=updateSettings', { method: 'POST', body: JSON.stringify(settings) });
export const getLegalContent = () => request<LegalContent>('?action=getLegal');
export const getActivityLog = () => request<ActivityLog[]>('?action=getActivityLog');
export const getUsers = () => request<User[]>('?action=getUsers');
export const createUser = (data: any, _user?: User) => request<User>('?action=createUser', { method: 'POST', body: JSON.stringify(data) });
export const updateUser = (id: string, data: any, _user?: User) => request<User>(`?action=updateUser&id=${id}`, { method: 'POST', body: JSON.stringify(data) });
export const deleteUser = (id: string, _user?: User) => request<void>(`?action=deleteUser&id=${id}`, { method: 'POST' });
export const getRoles = () => request<any[]>('?action=getRoles');
export const getPermissions = () => request<string[]>('?action=getPermissions');
// FIX: Update createRole signature to accept User parameter for consistency.
export const createRole = (data: any, user: User) => request<void>('?action=createRole', { method: 'POST', body: JSON.stringify(data) });
// FIX: Update updateRole signature to accept User parameter for consistency.
export const updateRole = (id: string, data: any, user: User) => request<void>(`?action=updateRole&id=${id}`, { method: 'POST', body: JSON.stringify(data) });
export const getDashboardStats = (user: User, organizationId?: string) => request<any>(`?action=getDashboardStats&organizationId=${organizationId || 'all'}`);
export const getEntityDetails = (id: string) => request<any>(`?action=getEntity&id=${id}`);
export const getIncidentsByEntity = (id: string) => request<Incident[]>(`?action=getIncidentsByEntity&id=${id}`);
export const getAllEntities = () => request<any[]>('?action=getAllEntities');
export const registerOrganization = (data: any, password: string, agreed: boolean) => request<Organization>('?action=register', { method: 'POST', body: JSON.stringify({ ...data, password, agreed }) });
export const testSmtpConnection = (settings: any) => request<string>('?action=testSmtp', { method: 'POST', body: JSON.stringify(settings) });
export const sendTestEmail = (email: string, settings: any) => request<string>('?action=sendTestEmail', { method: 'POST', body: JSON.stringify({ email, settings }) });
